<template>
  <div class="users-list">
    <v-banner>
      <v-avatar slot="icon" size="40">
        <v-icon>info</v-icon>
      </v-avatar>
      <div class="title-1">Tips</div>
      <div v-if="displayInfo" class="caption">
        <span>This page is only visible by organization administrators. </span>
        <span>As an organization administrator, you can:</span>
        <ul>
          <li><strong>Add a new user</strong> to your organization</li>
          <li><strong>Remove an existing user</strong> from your organization</li>
          <li><strong>Manage existing users</strong> organization accesses</li>
        </ul>
        <div>
          <span>Roles are:</span>
        </div>
        <ul>
          <li><strong>Admin</strong>: can manage users and their organization accesses, and editor and viewers rights</li>
          <li><strong>Editor</strong>: can grant concepts and locations access to an organization user, can create group to give access to applications to specific users</li>
          <li><strong>Viewer</strong>: can only view its own accesses and profile</li>
        </ul>
      </div>
      <template v-slot:actions>
        <v-btn text color="dark" @click="displayInfo = !displayInfo">{{displayInfo ?'Hide':'Show'}}</v-btn>
      </template>
    </v-banner>
    <v-container>
      <v-card>
        <v-data-table
          :headers="headers"
          :items="users ? users : []"
          :search="search"
          :loading="isLoading"
        >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-btn icon small color="primary" class="ma-2" @click="refresh">
                <v-icon>refresh</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn color="success" @click="openUserCreationModal">
                <v-icon>add</v-icon>
                <span>Create</span>
              </v-btn>
            </v-toolbar>
            <v-card-title>
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Search"
                single-line
                hide-details
              ></v-text-field>
            </v-card-title>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon color="primary">
              <v-icon @click="openUserDialog(item)">edit</v-icon>
            </v-btn>
            <v-btn icon color="red">
              <v-icon @click="removeUser(item)">delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
      <v-dialog v-model="userDialog" max-width="600px" ref="userDialog">
        <v-card>
          <v-card-title>Manage user</v-card-title>
          <v-card-text>
            <v-row>
              <v-banner icon="info">
                You can grant roles to users with the following buttons.
                <ul>
                  <li>Admin role: granting the permission to grant roles to other users</li>
                  <li>Editor role: granting the permission to manage rights and groups</li>
                  <li>Viewer role: granting only the permission to access their organization, roles and groups</li>
                </ul>
              </v-banner>
            </v-row>
            <v-row class="d-flex align-center">
              <v-col cols="12" md="6">
                <v-text-field
                  :value="selectedUser.last_name ? selectedUser.last_name.toUpperCase() + ' ' + selectedUser.first_name : ''"
                  disabled
                  prepend-icon="face"
                ></v-text-field>
                <v-text-field :value="selectedUser.email" disabled prepend-icon="email"></v-text-field>
              </v-col>
              <v-col class="d-flex justify-end align-center">
                <v-list>
                  <v-list-item>
                    <v-btn
                      v-if="!selectedUser.roles.includes('admins')"
                      small
                      color="primary"
                      @click="addToAdmins"
                      :loading="isLoading"
                    >
                      <v-icon left small>verified_user</v-icon>
                      <span>Add to admins</span>
                    </v-btn>
                    <v-btn
                      v-else
                      small
                      color="error"
                      @click="removeFromAdmins"
                      :loading="isLoading"
                    >
                      <v-icon left small>verified_user</v-icon>
                      <span>Remove from admins</span>
                    </v-btn>
                  </v-list-item>
                  <v-list-item>
                    <v-btn
                      v-if="!selectedUser.roles.includes('editors')"
                      small
                      color="primary"
                      @click="addToEditors"
                      :loading="isLoading"
                    >
                      <v-icon left small>assignment_ind</v-icon>
                      <span>Add to editors</span>
                    </v-btn>
                    <v-btn
                      v-else
                      small
                      color="error"
                      @click="removeFromEditors"
                      :loading="isLoading"
                    >
                      <v-icon left small>assignment_ind</v-icon>
                      <span>Remove from editors</span>
                    </v-btn>
                  </v-list-item>
                  <v-list-item>
                    <v-btn
                      v-if="!selectedUser.roles.includes('viewers')"
                      small
                      color="primary"
                      @click="addToViewers"
                      :loading="isLoading"
                    >
                      <v-icon left small>face</v-icon>
                      <span>Add to viewers</span>
                    </v-btn>
                    <v-btn
                      v-else
                      small
                      color="error"
                      @click="removeFromViewers"
                      :loading="isLoading"
                    >
                      <v-icon left small>face</v-icon>
                      <span>Remove from viewers</span>
                    </v-btn>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
    <v-dialog v-model="openOrgaSelector" persistent max-width="600">
      <v-card>
        <v-card-title>Please select your organization</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item v-for="orga in organizations" :key="orga.uuid">
              <v-list-item-icon>
                <v-icon>apartment</v-icon>
              </v-list-item-icon>
              <v-list-item-title>
                <v-list-item-content>{{orga.name}}</v-list-item-content>
              </v-list-item-title>
              <v-list-item-action>
                <v-btn color="success" @click="selectOrganization(orga)">Select</v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="userCreationDialog" persistent max-width="600">
      <UserCreationDialog v-on:cancel="userCreationDialog = false" v-on:submit="createUser" />
    </v-dialog>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import UserCreationDialog from "@/components/Access/Users/UserCreationDialog";

export default {
  components: {
    UserCreationDialog
  },
  mixins: [mixin],
  data() {
    return {
      search: "",
      userDialog: null,
      selectedUser: { roles: [] },
      snackbar: false,
      userCreationDialog: false,
      openOrgaSelector: false,
      errorMessage: "",
      displayInfo: false,
      headers: [
        {
          text: "First Name",
          align: "start",
          filterable: true,
          value: "first_name",
          width: "20%"
        },
        {
          text: "Last Name",
          align: "start",
          filterable: true,
          value: "last_name",
          width: "20%"
        },
        {
          text: "Email",
          align: "start",
          filterable: true,
          value: "email",
          width: "30%"
        },
        { text: "Actions", value: "actions", sortable: false, align: "right" }
      ]
    };
  },
  computed: {
    ...mapState({
      currentOrganization: state => state.organizations.current,
      organizations: state => state.organizations.all,
      admins: state => state.organizations.admins,
      editors: state => state.organizations.editors,
      viewers: state => state.organizations.viewers,
      adminsLoading: state => state.organizations.fetchAllAdmins.pending,
      editorsLoading: state => state.organizations.fetchAllEditors.pending,
      viewersLoading: state => state.organizations.fetchAllViewers.pending
    }),
    users() {
      var users = [];
      this.admins.forEach(admin => {
        users.push({ ...admin, roles: ["admins"] });
      });
      this.editors.forEach(editor => {
        if (users.map(user => user.uuid).includes(editor.uuid)) {
          let userIndex = users.map(user => user.uuid).indexOf(editor.uuid);
          users[userIndex]["roles"].push("editors");
        } else {
          users.push({ ...editor, roles: ["editors"] });
        }
      });
      this.viewers.forEach(viewer => {
        if (users.map(user => user.uuid).includes(viewer.uuid)) {
          let userIndex = users.map(user => user.uuid).indexOf(viewer.uuid);
          users[userIndex]["roles"].push("viewers");
        } else {
          users.push({ ...viewer, roles: ["viewers"] });
        }
      });
      return users;
    },
    isLoading() {
      return this.adminsLoading && this.editorsLoading && this.viewersLoading;
    }
  },
  watch: {
    currentOrganization() {
      this.refresh();
    }
  },
  methods: {
    ...mapActions("organizations", {
      setCurrentOrganization: "setCurrentOrganization",
      fetchAllAdmins: "fetchAllAdmins",
      fetchAllEditors: "fetchAllEditors",
      fetchAllViewers: "fetchAllViewers",
      addAdmin: "addAdmin",
      removeAdmin: "removeAdmin",
      addEditor: "addEditor",
      removeEditor: "removeEditor",
      addViewer: "addViewer",
      removeViewer: "removeViewer",
      createOneUser: "createOneUser",
      removeOneUser: "removeOneUser"
    }),
    selectOrganization(organization) {
      this.setCurrentOrganization({ organization: organization });
      this.openOrgaSelector = false;
    },
    groupedRefresh() {
      return Promise.all([
        this.fetchAllAdmins({
          organizationUuid: this.currentOrganization.uuid
        }),
        this.fetchAllEditors({
          organizationUuid: this.currentOrganization.uuid
        }),
        this.fetchAllViewers({
          organizationUuid: this.currentOrganization.uuid
        })
      ]);
    },
    refresh() {
      this.groupedRefresh().catch(error => {
        this.snackbar = true;
        this.errorMessage = this.errorDisplay(error);
      });
    },
    openUserDialog(user) {
      this.selectedUser = user;
      this.userDialog = true;
    },
    refreshAfterButton() {
      this.groupedRefresh()
        .then(() => {
          var user = this.users.find(
            user => this.selectedUser.uuid === user.uuid
          );
          if (user) {
            this.selectedUser = user;
          } else {
            this.selectedUser = { roles: [] };
            this.userDialog = false;
          }
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    openUserCreationModal() {
      this.userCreationDialog = true;
    },
    createUser(lastName, firstName, email) {
      this.createOneUser({
        organizationUuid: this.currentOrganization.uuid,
        lastName: lastName,
        firstName: firstName,
        email: email
      })
        .then(() => {
          this.refresh();
          this.userCreationDialog = false;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeUser(user) {
      this.removeOneUser({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: user.uuid
      })
        .then(() => {
          this.refresh();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addToAdmins() {
      this.addAdmin({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.selectedUser.uuid
      })
        .then(() => {
          this.refreshAfterButton();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeFromAdmins() {
      this.removeAdmin({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.selectedUser.uuid
      })
        .then(() => {
          this.refreshAfterButton();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addToEditors() {
      this.addEditor({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.selectedUser.uuid
      })
        .then(() => {
          this.refreshAfterButton();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeFromEditors() {
      this.removeEditor({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.selectedUser.uuid
      })
        .then(() => {
          this.refreshAfterButton();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addToViewers() {
      this.addViewer({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.selectedUser.uuid
      })
        .then(() => {
          this.refreshAfterButton();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeFromViewers() {
      this.removeViewer({
        organizationUuid: this.currentOrganization.uuid,
        userUuid: this.selectedUser.uuid
      })
        .then(() => {
          this.refreshAfterButton();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    }
  },
  created() {
    if (this.currentOrganization) {
      this.refresh();
    } else {
      this.openOrgaSelector = true;
    }
  }
};
</script>